import { gql } from "@apollo/client";
import { CardContainer, CollapseSection, useFormatting } from "@msys/ui";
import { Stack, Typography } from "@mui/material";
import { isNil } from "lodash-es";
import { OrderDetailsFragment } from "./OrderDetailsBox.generated.js";
import { useTranslate } from "@tolgee/react";

interface Props {
  billOfMaterialsItem: OrderDetailsFragment;
}

export const OrderDetailsBox = ({ billOfMaterialsItem }: Props) => {
  const { t } = useTranslate("Materials");
  const { getFormattedPrice } = useFormatting();

  return (
    <CardContainer title={t("Order details")} isExpandable>
      <Stack
        direction="column"
        spacing={0}
        sx={{
          margin: 1,
        }}
      >
        <CollapseSection
          title={t("Material quantity")}
          titleVariant="h4"
          isInitiallyExpanded
        >
          <Stack
            spacing={1}
            direction={"column"}
            sx={{
              padding: 1,
            }}
          >
            <Stack
              spacing={1}
              direction="row"
              sx={{
                justifyContent: "space-between",
              }}
            >
              <Typography>{"Total required"}</Typography>
              <Typography>{`${billOfMaterialsItem.quantityRequiredTotal} ${billOfMaterialsItem.quantityUnit}`}</Typography>
            </Stack>
            <Stack
              spacing={1}
              direction="column"
              sx={{
                paddingLeft: 1,
              }}
            >
              {billOfMaterialsItem.quantityRequiredAccepted !== 0 && (
                <Stack
                  spacing={1}
                  direction="row"
                  sx={{
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="body2">
                    {t("Accepted quote items")}
                  </Typography>
                  <Typography variant="body2">
                    {`${billOfMaterialsItem.quantityRequiredAccepted} ${billOfMaterialsItem.quantityUnit}`}
                  </Typography>
                </Stack>
              )}
              {billOfMaterialsItem.quantityRequiredDraft !== 0 && (
                <Stack
                  spacing={1}
                  direction="row"
                  sx={{
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="body2">
                    {t("Draft quote items")}
                  </Typography>
                  <Typography variant="body2">
                    {`${billOfMaterialsItem.quantityRequiredDraft} ${billOfMaterialsItem.quantityUnit}`}
                  </Typography>
                </Stack>
              )}
              {billOfMaterialsItem.quantityRequiredPending !== 0 && (
                <Stack
                  spacing={1}
                  direction="row"
                  sx={{
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="body2">
                    {t("Pending quote items")}
                  </Typography>
                  <Typography variant="body2">
                    {`${billOfMaterialsItem.quantityRequiredPending} ${billOfMaterialsItem.quantityUnit}`}
                  </Typography>
                </Stack>
              )}
              {billOfMaterialsItem.quantityRequiredChange !== 0 && (
                <Stack
                  spacing={1}
                  direction="row"
                  sx={{
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="body2">
                    {t("Items with pending change order")}
                  </Typography>
                  <Typography variant="body2">
                    {`${billOfMaterialsItem.quantityRequiredChange} ${billOfMaterialsItem.quantityUnit}`}
                  </Typography>
                </Stack>
              )}
            </Stack>
            <Stack
              spacing={1}
              direction="row"
              sx={{
                justifyContent: "space-between",
              }}
            >
              <Typography>{t("Ordered")}</Typography>
              <Typography>{`${billOfMaterialsItem.quantityOrdered} ${billOfMaterialsItem.quantityUnit}`}</Typography>
            </Stack>
            <Stack
              spacing={1}
              direction="row"
              sx={{
                justifyContent: "space-between",
              }}
            >
              <Typography>{t("To be ordered")}</Typography>
              <Typography>{`${Math.max(
                billOfMaterialsItem.quantityRequiredTotal -
                  billOfMaterialsItem.quantityOrdered,
                0
              )} ${billOfMaterialsItem.quantityUnit}`}</Typography>
            </Stack>
          </Stack>
        </CollapseSection>
        <CollapseSection
          title={t("Material pricing")}
          titleVariant="h4"
          isInitiallyExpanded
        >
          <Stack
            spacing={1}
            direction={"column"}
            sx={{
              padding: 1,
            }}
          >
            <Stack
              spacing={1}
              direction="row"
              sx={{
                justifyContent: "space-between",
              }}
            >
              <Typography>{t("List price")}</Typography>
              <Typography>
                {!isNil(billOfMaterialsItem.listPrice)
                  ? getFormattedPrice(billOfMaterialsItem.listPrice)
                  : "-"}
              </Typography>
            </Stack>
            <Stack
              spacing={1}
              direction="row"
              sx={{
                justifyContent: "space-between",
              }}
            >
              <Typography>{t("Net price")}</Typography>
              <Typography>
                {!isNil(billOfMaterialsItem.netPrice)
                  ? getFormattedPrice(billOfMaterialsItem.netPrice)
                  : "-"}
              </Typography>
            </Stack>
          </Stack>
        </CollapseSection>
      </Stack>
    </CardContainer>
  );
};
